/**
 * Blog Post Hero Section
 *
 * Full-width hero image with title overlay for blog post details page
 */
'use client';

import React from 'react';
import Image from 'next/image';
import { Download } from 'lucide-react';

interface BlogPostHeroProps {
  title: string;
  image: string;
}

export default function BlogPostHero({ title, image }: BlogPostHeroProps) {
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = image;
    link.download = `${title.toLowerCase().replace(/\s+/g, '-')}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="relative w-full h-[788px] overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <Image src={image} alt={title} fill className="object-cover" priority />
        {/* Dark overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />

        {/* Additional blur effect */}
        <div className="absolute inset-0 backdrop-blur-[2px]" />
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center px-6 md:px-12 lg:px-24">
        <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white text-center max-w-5xl leading-tight tracking-tight font-manrope">
          {title}
        </h1>

        {/* Download Button */}
        <button
          onClick={handleDownload}
          className="mt-16 inline-flex items-center gap-2 px-12 py-6 rounded-full border-2 border-white/10 bg-white/30 backdrop-blur-[15px] hover:bg-white/40 transition-all duration-300 group"
        >
          <Download className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
          <span className="text-lg font-semibold text-white font-manrope">Download Image</span>
        </button>
      </div>
    </section>
  );
}
